import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, formatApiError, resolveImageUrl } from "../../lib/adminApi";
import { Plus, Pencil, Trash2, Loader2, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";

export default function AdminProjectsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/projects");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (p) => {
    if (!window.confirm(`¿Eliminar el proyecto "${p.name}"?`)) return;
    try {
      await adminApi.delete(`/projects/${p.id}`);
      toast.success("Proyecto eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onMove = async (p) => {
    const target = p.category === "residencias" ? "enclaves" : "residencias";
    if (!window.confirm(`¿Mover "${p.name}" a ${target}?`)) return;
    try {
      await adminApi.put(`/projects/${p.id}`, {
        category: target,
        name: p.name,
        number: p.number,
        location: p.location,
        hero: p.hero,
        summary: p.summary || "",
        description: p.description || "",
        thumbs: p.thumbs || [],
        order: p.order || 0,
      });
      toast.success(`Movido a ${target}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const visible = items.filter((p) => filter === "all" || p.category === filter);

  return (
    <section className="px-10 py-12" data-testid="admin-projects-list">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div
            className="text-[var(--pc-bronze)]"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: 11,
              letterSpacing: "0.34em",
              textTransform: "uppercase",
            }}
          >
            Obra
          </div>
          <h1
            className="headline-xl mt-3 text-[var(--pc-text)]"
            style={{ fontSize: "clamp(26px, 2.6vw, 38px)", letterSpacing: "0.16em", lineHeight: 1.2 }}
          >
            Proyectos
          </h1>
          <div className="h-px w-10 bg-[var(--pc-bronze)] mt-5 opacity-90" />
        </div>

        <Link to="/admin/proyectos/nuevo" className="admin-btn-primary" data-testid="new-project-btn">
          <Plus size={14} className="inline mr-2" />
          Nuevo proyecto
        </Link>
      </div>

      {/* Filter */}
      <div className="flex gap-1 mt-10 border-b border-[var(--pc-line)]">
        {[
          ["all", "Todos"],
          ["residencias", "Residencias"],
          ["enclaves", "Enclaves"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`filter-${k}`}
            className={`px-4 py-3 border-b-2 -mb-px transition-colors ${
              filter === k
                ? "border-[var(--pc-bronze)] text-[var(--pc-text)]"
                : "border-transparent text-[var(--pc-text-soft)] hover:text-[var(--pc-text)]"
            }`}
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: 11,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
            }}
          >
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="text-[var(--pc-bronze)] animate-spin" />
        </div>
      ) : visible.length === 0 ? (
        <p className="mt-12 text-[var(--pc-text-soft)]" style={{ fontFamily: "Inter", fontSize: 13 }}>
          Aún no hay proyectos. Crea el primero con el botón &quot;Nuevo proyecto&quot;.
        </p>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {visible.map((p) => (
            <div
              key={p.id}
              className="border border-[var(--pc-line)] bg-[#0b0b0d] group"
              data-testid={`project-card-${p.slug}`}
            >
              <div
                className="relative overflow-hidden bg-[#15151a]"
                style={{ aspectRatio: "4/3" }}
              >
                {p.hero ? (
                  <img
                    src={resolveImageUrl(p.hero)}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-[var(--pc-text-soft)] text-xs">
                    Sin imagen
                  </div>
                )}
              </div>
              <div className="p-5">
                <div
                  className="text-[var(--pc-bronze)]"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 400,
                    fontSize: 10,
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                  }}
                >
                  {p.category} · {p.number}
                </div>
                <h3
                  className="mt-2 text-[var(--pc-text)]"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 300,
                    fontSize: 17,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}
                >
                  {p.name}
                </h3>
                <p
                  className="text-[var(--pc-text-soft)] mt-1"
                  style={{ fontFamily: "Inter", fontSize: 12, letterSpacing: "0.06em" }}
                >
                  {p.location}
                </p>
                <div className="flex flex-wrap gap-2 mt-5">
                  <Link
                    to={`/admin/proyectos/${p.id}`}
                    className="admin-btn-ghost flex items-center gap-2 text-xs"
                    data-testid={`edit-project-${p.slug}`}
                  >
                    <Pencil size={12} /> Editar
                  </Link>
                  <button
                    onClick={() => onMove(p)}
                    className="admin-btn-ghost flex items-center gap-2 text-xs"
                    data-testid={`move-project-${p.slug}`}
                    title={`Mover a ${p.category === "residencias" ? "Enclaves" : "Residencias"}`}
                  >
                    <ArrowLeftRight size={12} />
                    {p.category === "residencias" ? "→ Enclaves" : "→ Residencias"}
                  </button>
                  <button
                    onClick={() => onDelete(p)}
                    className="admin-btn-ghost flex items-center gap-2 text-xs text-red-300 hover:text-red-200"
                    data-testid={`delete-project-${p.slug}`}
                  >
                    <Trash2 size={12} /> Eliminar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
